/**
 * <product-title-slider>
 * Cycles through stacked phrases with a pause → slide → pause rhythm.
 * Clones the first item to create a seamless infinite loop.
 */
class ProductTitleSlider extends HTMLElement {
  /** @type {Element | null} */
  viewport = null;
  /** @type {HTMLElement | null} */
  track = null;
  /** @type {Element[]} */
  items = [];
  count = 0;
  pauseMs = 1000;
  moveMs = 500;
  index = 0;
  stepPx = 0;
  _paused = false;
  /** @type {number | null} */
  _timer = null;
  /** @type {ResizeObserver | null} */
  _resizeObserver = null;
  _initialized = false;

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.viewport = this.querySelector('[data-pta-viewport]');
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-pta-list]'));
    if (!this.viewport || !this.track) return;

    this.items = Array.from(this.track.children);
    this.count = this.items.length;
    if (this.count < 2) return;

    this.pauseMs = (parseFloat(this.dataset.pause || '1') || 1) * 1000;
    this.moveMs = (parseFloat(this.dataset.transition || '0.5') || 0.5) * 1000;

    const firstItem = this.items[0];
    if (firstItem) this.track.appendChild(firstItem.cloneNode(true));

    requestAnimationFrame(() => {
      this._measure();
      this._scheduleNext();
    });

    this._resizeObserver = new ResizeObserver(() => this._measure());
    if (this.viewport) this._resizeObserver.observe(this.viewport);

    this.addEventListener('mouseenter', this._onEnter);
    this.addEventListener('mouseleave', this._onLeave);
  }

  disconnectedCallback() {
    this._clearTimer();
    this._resizeObserver?.disconnect();
    this.removeEventListener('mouseenter', this._onEnter);
    this.removeEventListener('mouseleave', this._onLeave);
  }

  _onEnter = () => {
    this._paused = true;
    this._clearTimer();
  };

  _onLeave = () => {
    this._paused = false;
    this._scheduleNext();
  };

  _clearTimer() {
    if (this._timer != null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  _measure() {
    if (!this.track) return;

    const first = this.track.firstElementChild;
    if (!first) return;

    this.stepPx = first.getBoundingClientRect().height;

    this.track.style.transition = 'none';
    this.track.style.transform = `translateY(${-this.index * this.stepPx}px)`;
    void this.track.offsetHeight; // force reflow before next transition
  }

  _scheduleNext() {
    if (this._paused) return;
    this._timer = setTimeout(() => this._slide(), this.pauseMs);
  }

  _slide() {
    if (this._paused || !this.track) return;

    this.index += 1;
    const track = this.track;

    track.style.transition = `transform ${this.moveMs}ms cubic-bezier(0.25, 1, 0.5, 1)`;
    track.style.transform = `translateY(${-this.index * this.stepPx}px)`;

    // Small buffer after CSS transition completes before resetting or scheduling
    this._timer = setTimeout(() => {
      if (this.index >= this.count) {
        this.index = 0;
        track.style.transition = 'none';
        track.style.transform = 'translateY(0)';
        void track.offsetHeight; // force reflow for instant reset
      }

      this._scheduleNext();
    }, this.moveMs + 30);
  }
}

/* ── Mobile auto-fit ────────────────────────────────────────────── */

const MOBILE_FIT_SELECTOR = '.product-title-animated[data-pta-mobile-fit="true"]';

// Slight reduction to prevent sub-pixel overflow at the binary-search boundary
const SAFETY_SCALE = 0.99;

/**
 * Binary-searches for the largest font size (via CSS custom property)
 * that fits all title lines within their container on mobile viewports.
 */
class ProductTitleMobileFit {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.lines = Array.from(root.querySelectorAll('[data-pta-fit-line]'));
    this.resizeObserver = null;
    this.raf = null;
    this._onResize = () => this.schedule();
    this._lastFittedWidth = -1;
  }

  connect() {
    if (!this.lines.length) return;

    this.resizeObserver = new ResizeObserver(() => this.schedule());
    this.resizeObserver.observe(this.root);

    window.addEventListener('resize', this._onResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this._lastFittedWidth = -1;
        this.schedule();
      });
    }

    this.schedule();
  }

  disconnect() {
    if (this.raf != null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    window.removeEventListener('resize', this._onResize);
    this.root.removeAttribute('data-pta-fitted');
    this.root.style.removeProperty('--pta-mobile-fit-size');
    this._lastFittedWidth = -1;
  }

  schedule() {
    if (this.raf != null) cancelAnimationFrame(this.raf);

    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this._fit();
    });
  }

  _fit() {
    if (!this.root.isConnected) return;

    const containerWidth = this.root.clientWidth;
    if (containerWidth <= 0 || containerWidth === this._lastFittedWidth) return;

    // Clear fitted state to measure at the natural (CSS-cascade) font-size
    this.root.removeAttribute('data-pta-fitted');
    this.root.style.removeProperty('--pta-mobile-fit-size');

    const overflows = this.lines.some((line) => line.scrollWidth > containerWidth);

    if (!overflows) {
      this._lastFittedWidth = containerWidth;
      return;
    }

    // Use the current computed font-size as the search ceiling
    const naturalSize = parseFloat(getComputedStyle(this.root).fontSize) || 500;

    this.root.setAttribute('data-pta-fitted', '');

    let lo = 1;
    let hi = naturalSize;
    let best = 1;

    for (let i = 0; i < 30 && hi - lo > 0.5; i++) {
      const mid = (lo + hi) / 2;
      this.root.style.setProperty('--pta-mobile-fit-size', `${mid}px`);

      if (this.lines.some((line) => line.scrollWidth > containerWidth)) {
        hi = mid;
      } else {
        lo = mid;
        best = mid;
      }
    }

    const finalSize = `${best * SAFETY_SCALE}px`;
    if (this.root.style.getPropertyValue('--pta-mobile-fit-size') !== finalSize) {
      this.root.style.setProperty('--pta-mobile-fit-size', finalSize);
    }

    this._lastFittedWidth = containerWidth;
  }
}

/* ── Registry & bootstrap ───────────────────────────────────────── */

/** @type {Map<Element, ProductTitleMobileFit>} */
const mobileFitRegistry = new Map();

/**
 * @param {Document | Element} context
 * @returns {Element[]}
 */
function collectMobileFitRoots(context) {
  if (!(context instanceof Element || context instanceof Document)) return [];

  /** @type {Element[]} */
  const roots = [];
  if (context instanceof Element && context.matches(MOBILE_FIT_SELECTOR)) {
    roots.push(context);
  }

  roots.push(...context.querySelectorAll(MOBILE_FIT_SELECTOR));
  return roots;
}

/** @param {Document | Element} [context] */
function initMobileFit(context = document) {
  for (const root of collectMobileFitRoots(context)) {
    if (mobileFitRegistry.has(root)) continue;

    const instance = new ProductTitleMobileFit(/** @type {HTMLElement} */ (root));
    mobileFitRegistry.set(root, instance);
    instance.connect();
  }
}

/** @param {Document | Element} [context] */
function destroyMobileFit(context = document) {
  for (const root of collectMobileFitRoots(context)) {
    const instance = mobileFitRegistry.get(root);
    if (!instance) continue;

    instance.disconnect();
    mobileFitRegistry.delete(root);
  }
}

if (!customElements.get('product-title-slider')) {
  customElements.define('product-title-slider', ProductTitleSlider);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMobileFit());
} else {
  initMobileFit();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initMobileFit(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  if (event.target instanceof Element) initMobileFit(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof Element) destroyMobileFit(event.target);
});
